import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { ImageIcon, ExternalLink, Plus, Images, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import Button from "@/components/Button";
import ProjectModal from "@/components/ProjectModal";
import GalleryModal from "@/components/GalleryModal";
import { PORTFOLIO, PORTFOLIO_CATEGORIES, siteShot } from "@/data/site";
import { GALLERIES } from "@/data/galleries";
import { fadeUp, stagger } from "@/lib/motion";
import Seo from "@/components/Seo";

// Convierte cada galería fotográfica en un item del portafolio
const GALLERY_ITEMS = GALLERIES.map((g) => ({
  id: `galeria-${g.id}`,
  type: "gallery",
  title: g.title,
  category: "Fotografía",
  cover: g.cover,
  images: g.images,
  count: g.count,
}));

const ALL_ITEMS = [...PORTFOLIO, ...GALLERY_ITEMS];

const Portafolio = () => {
  const [active, setActive] = useState("Todos");
  const [selected, setSelected] = useState(null);
  const [gallery, setGallery] = useState(null);

  const items = useMemo(
    () => (active === "Todos" ? ALL_ITEMS : ALL_ITEMS.filter((p) => p.category === active)),
    [active]
  );

  const openItem = (item) => (item.type === "gallery" ? setGallery(item) : setSelected(item));

  return (
    <div data-testid="portafolio-page">
      <Seo
        title="Portafolio"
        description="Explora el portafolio de Lu Design: sitios web, invitaciones digitales, diseño gráfico y galerías de fotografía profesional."
        path="/portafolio"
      />
      <PageHero
        overline="Portafolio"
        titleLines={["Historias", "hechas diseño."]}
        intro="Explora nuestro trabajo por categoría. Algunos proyectos ya están aquí; otros llegarán muy pronto. Da clic en cualquiera para verlo de cerca."
        testId="portafolio-hero"
      />

      <section className="relative z-10 bg-ivory pb-24 md:pb-32">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          {/* Filtro de categorías */}
          <div
            className="mb-12 flex flex-wrap gap-x-2 gap-y-3 border-b border-rosegold/20 pb-6"
            data-testid="portfolio-filters"
          >
            {PORTFOLIO_CATEGORIES.map((cat) => {
              const isActive = active === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActive(cat)}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative rounded-full px-5 py-2.5 text-xs md:text-sm uppercase tracking-[0.15em] transition-[color,background-color] duration-500 ${
                    isActive
                      ? "bg-plum text-ivory"
                      : "text-charcoal/60 hover:text-plum"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {/* Grid (se re-monta al cambiar de filtro para una entrada limpia) */}
          <motion.div
            key={active}
            variants={stagger(0, 0.08)}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            {items.map((item) => (
              <motion.button
                key={item.id}
                variants={fadeUp}
                onClick={() => openItem(item)}
                data-testid={`portfolio-item-${item.id}`}
                className="group relative block overflow-hidden text-left"
              >
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-powder/40">
                  {item.type === "web" ? (
                    <img
                      src={siteShot(item.url)}
                      alt={item.title}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : item.type === "gallery" ? (
                    <img
                      src={item.cover}
                      alt={item.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center bg-powder/50 transition-transform duration-700 ease-out group-hover:scale-105">
                      <ImageIcon className="h-10 w-10 text-plum/40" strokeWidth={1} />
                      <span className="mt-4 text-[0.6rem] uppercase tracking-[0.3em] text-plum/60">
                        Próximamente
                      </span>
                    </div>
                  )}

                  {/* Badge de conteo para galerías */}
                  {item.type === "gallery" && (
                    <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-ivory/85 px-3 py-1 text-[0.6rem] uppercase tracking-[0.15em] text-plum backdrop-blur">
                      <Images className="h-3 w-3" strokeWidth={1.5} />
                      {item.count} fotos
                    </span>
                  )}

                  {/* Overlay */}
                  <div className="pointer-events-none absolute inset-0 flex flex-col justify-end bg-plum/0 p-6 transition-colors duration-500 group-hover:bg-plum/55">
                    <div className="translate-y-4 opacity-0 transition-[transform,opacity] duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100">
                      <p className="text-[0.55rem] uppercase tracking-[0.3em] text-rosegold">
                        {item.category}
                      </p>
                      <h3 className="mt-1.5 font-serif text-2xl text-ivory">{item.title}</h3>
                    </div>
                  </div>

                  {/* Icono esquina */}
                  <div className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-ivory/85 text-plum opacity-0 backdrop-blur transition-opacity duration-500 group-hover:opacity-100">
                    {item.type === "web" ? (
                      <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
                    ) : item.type === "gallery" ? (
                      <Images className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </div>
                </div>

                {/* Pie visible siempre */}
                <div className="flex items-baseline justify-between pt-4">
                  <span className="font-serif text-lg text-plum">{item.title}</span>
                  <span className="text-[0.6rem] uppercase tracking-[0.2em] text-charcoal/40">
                    {item.category}
                  </span>
                </div>
              </motion.button>
            ))}
          </motion.div>

          {items.length === 0 && (
            <div className="flex flex-col items-center py-24 text-center" data-testid="portfolio-empty">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-rosegold/40 text-rosegold">
                <Sparkles className="h-6 w-6" strokeWidth={1.25} />
              </div>
              <p className="mt-8 font-serif italic text-3xl md:text-4xl text-plum">Próximamente</p>
              <p className="mt-4 max-w-md font-sans font-light text-charcoal/60">
                Estamos preparando proyectos preciosos para esta categoría. Muy pronto tendrán su
                lugar aquí.
              </p>
            </div>
          )}

          <div className="mt-20 flex flex-col items-center gap-6 text-center">
            <p className="max-w-md font-sans font-light text-charcoal/60">
              ¿Quieres ser parte del portafolio? Tu proyecto podría ser la próxima historia.
            </p>
            <Button to="/contacto" variant="outline" testId="portafolio-cta">
              Iniciar un proyecto
            </Button>
          </div>
        </div>
      </section>

      <ProjectModal project={selected} onClose={() => setSelected(null)} />
      <GalleryModal gallery={gallery} onClose={() => setGallery(null)} />
    </div>
  );
};

export default Portafolio;
